// The check-in alarm overlay (architecture sprint-14 §8.3). A full-viewport portal above Radix dialogs. Only the
// button confirms: Esc and backdrop clicks do nothing. role="alertdialog" keeps the global shortcuts off while it
// shows (lib/keyboard-shortcuts.ts). Theme tokens only (raw-colors guard).
import { AlarmClock } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';

export const IM_AWAKE = "I'm awake";

interface AlarmOverlayProps {
  /** 'HH:MM' of the newest ringing mark, in the viewer's zone. */
  markLabel: string;
  /** 'HH:MM' of its deadline. */
  deadlineLabel: string;
  overdue: boolean;
  onConfirm: () => void;
}

export function AlarmOverlay({ markLabel, deadlineLabel, overdue, onConfirm }: AlarmOverlayProps): JSX.Element {
  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alarm-overlay-title"
      aria-describedby="alarm-overlay-deadline"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 p-4"
      onKeyDown={(e) => {
        if (e.key === 'Escape') e.preventDefault();
      }}
    >
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-border bg-card p-6 text-center">
        <AlarmClock className="mx-auto h-12 w-12 text-primary" aria-hidden="true" />
        <div className="space-y-1">
          <h2 id="alarm-overlay-title" className="text-2xl font-normal text-foreground">
            Check-in · {markLabel}
          </h2>
          <p id="alarm-overlay-deadline" className={overdue ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {overdue ? `Overdue since ${deadlineLabel}` : `Confirm by ${deadlineLabel}`}
          </p>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the one action of a blocking alarm */}
        <Button type="button" autoFocus className="h-14 w-full text-lg" onClick={onConfirm}>
          {IM_AWAKE}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
